import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime as formatLocaleDateTime } from '../utils/dateFormat';

function formatDate(iso) {
    if (!iso) return '—';
    return formatLocaleDateTime(iso);
}

function parseErrors(details, t) {
    if (!details) return [];
    try {
        const parsed = JSON.parse(details);
        if (parsed?.rows && Array.isArray(parsed.rows)) {
            return parsed.rows
                .filter((e) => e.status === 'error' || e.status === 'skipped')
                .map((e) => {
                    const sheet = e.sheet ? `${e.sheet}, ` : '';
                    return `${sheet}стр. ${e.rowNumber}: ${e.note || e.status}`;
                });
        }
        if (Array.isArray(parsed)) {
            return parsed.map((e) =>
                t('importHistory:errorRow', { row: e.row, sheet: e.sheet, message: e.message })
            );
        }
        return [details];
    } catch {
        return [details];
    }
}

export default function ImportHistoryTable({ schoolId, api }) {
    const { t } = useTranslation(['importHistory', 'common']);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);

    useEffect(() => {
        load();
    }, [schoolId]);

    async function load() {
        setLoading(true);
        try {
            const res = await api.get(`/schools/${schoolId}/import/history`);
            setRows(res.data || []);
        } catch {
            setRows([]);
        } finally {
            setLoading(false);
        }
    }

    if (loading) return <p style={{ color: '#64748b' }}>{t('importHistory:loading')}</p>;
    if (!rows.length) return <p style={{ color: '#64748b' }}>{t('importHistory:empty')}</p>;

    return (
        <>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                    <tr style={{ borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                        <th style={{ padding: 10 }}>{t('importHistory:colDate')}</th>
                        <th style={{ padding: 10 }}>{t('importHistory:colFile')}</th>
                        <th style={{ padding: 10 }}>{t('importHistory:colAdded')}</th>
                        <th style={{ padding: 10 }}>{t('importHistory:colSkipped')}</th>
                        <th style={{ padding: 10 }}>{t('importHistory:colErrors')}</th>
                        <th style={{ padding: 10 }}>{t('importHistory:colStatus')}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr
                            key={r.id}
                            style={{ borderBottom: '1px solid #f1f5f9', cursor: r.errorDetails ? 'pointer' : 'default' }}
                            onClick={() => r.errorDetails && setDetail(r)}
                        >
                            <td style={{ padding: 10 }}>{formatDate(r.createdAt)}</td>
                            <td style={{ padding: 10 }}>{r.fileName}</td>
                            <td style={{ padding: 10 }}>{r.successCount}</td>
                            <td style={{ padding: 10 }}>{r.skippedCount}</td>
                            <td style={{ padding: 10 }}>{r.failedCount}</td>
                            <td style={{ padding: 10 }}>{r.status}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {detail && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.4)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1001
                    }}
                    onClick={() => setDetail(null)}
                >
                    <div
                        style={{
                            background: '#fff',
                            borderRadius: 12,
                            padding: 24,
                            maxWidth: 480,
                            maxHeight: '80vh',
                            overflow: 'auto'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 style={{ marginTop: 0 }}>
                            {t('importHistory:errorsTitle', { fileName: detail.fileName })}
                        </h3>
                        <ul style={{ fontSize: 13, paddingLeft: 18 }}>
                            {parseErrors(detail.errorDetails, t).map((line, i) => (
                                <li key={i} style={{ marginBottom: 6 }}>
                                    {line}
                                </li>
                            ))}
                        </ul>
                        <button
                            type="button"
                            onClick={() => setDetail(null)}
                            style={{
                                marginTop: 12,
                                padding: '8px 16px',
                                borderRadius: 8,
                                border: 'none',
                                background: '#1A1A1A',
                                color: '#fff',
                                cursor: 'pointer'
                            }}
                        >
                            {t('common:close')}
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
